import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO, {
  getWebPageSchema,
  getBreadcrumbSchema,
} from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';

const categoryNameMap: Record<string, string> = {
  dianying: '电影',
  dianshiju: '电视剧',
  zongyi: '综艺',
  dongman: '动漫',
  jilupian: '纪录片',
  yinyue: '音乐',
  youxi: '游戏',
  keji: '科技',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'dianying');

  useEffect(() => {
    if (slug) setActiveCategory(slug);
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === activeCategory);
  const categoryVideos = videos.filter((v) => v.category === activeCategory);
  const categoryName = categoryNameMap[activeCategory] || activeCategory;

  const title = `${categoryName} - 就要草视频 | 精选${categoryName}在线观看`;
  const description = `就要草视频${categoryName}分类，精选优质${categoryName}资源，高清流畅在线观看。汇聚最新最热${categoryName}内容。`;
  const keywords = `就要草视频${categoryName},${categoryName}在线观看,${categoryName}高清播放,${categoryName}免费观看,就要草视频`;
  const canonicalUrl = `https://jiuyaocao.com/category/${activeCategory}`;

  return (
    <>
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        jsonLd={[
          getWebPageSchema(title, description, canonicalUrl),
          getBreadcrumbSchema([
            { name: '首页', url: 'https://jiuyaocao.com/' },
            { name: categoryName, url: canonicalUrl },
          ]),
        ]}
      />

      <main>
        <div className="pt-20 pb-6 bg-background-100">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: categoryName },
            ]}
          />

          <div className="w-full px-4 md:px-6">
            <div className="max-w-6xl mx-auto">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
                {categoryName}
              </h1>
              <p className="text-sm text-foreground-600 mb-6">
                {currentCategory?.description || `精选${categoryName}视频内容`}
              </p>

              <div className="flex items-center gap-2 overflow-x-auto pb-3 flex-wrap">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className={`px-4 py-2 text-sm rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                      activeCategory === cat.slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-600 border border-background-200/70 hover:border-primary-300'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        <section className="w-full px-4 md:px-6 py-10">
          <div className="max-w-6xl mx-auto">
            {categoryVideos.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categoryVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/article/${video.id}`}
                    className="group bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative overflow-hidden aspect-video">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 就要草视频`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute bottom-2 right-2 bg-black/70 text-background-50 text-xs px-2 py-0.5 rounded">
                        {video.duration}
                      </div>
                      {video.isHot && (
                        <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded">
                          热门
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="text-sm font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors font-heading">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                        {video.description}
                      </p>
                      <div className="flex flex-wrap items-center gap-2 text-xs text-secondary-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line"></i>
                          {video.views >= 10000
                            ? `${(video.views / 10000).toFixed(1)}万`
                            : video.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500"></i>
                          {video.rating}
                        </span>
                        <span className="ml-auto">{video.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-secondary-100 rounded-full">
                  <i className="ri-video-line text-secondary-400 text-2xl"></i>
                </div>
                <p className="text-foreground-600">该分类暂无视频内容</p>
              </div>
            )}
          </div>
        </section>
      </main>
    </>
  );
}